// YouTube playlist parsing and URL building for the `youtube-playlist` block.
//
// A playlist reaches the app as whatever link the author had in the address
// bar, which is almost never the tidy /playlist?list=… form:
//
//   https://www.youtube.com/watch?v=IVjT3yGOdSU&list=PLUqoUMZA9Ix…&index=3
//
// Everything here works off that: the list id is what the card needs, the video
// id is where the author wants the card to open, and the index is what the
// author sees on YouTube. No DOM and no network — the module is shared by the
// app, the HTML export and the PowerPoint/PDF exporters.

const VIDEO_ID = /^[A-Za-z0-9_-]{11}$/;
// Playlist ids are longer and more varied than video ids (PL…, UU…, FL…, RD…,
// OLAK5uy_… for auto-generated album lists), so this stays deliberately loose.
const PLAYLIST_ID = /^[A-Za-z0-9_-]{12,64}$/;

const YOUTUBE_HOSTS = [
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'music.youtube.com',
  'youtube-nocookie.com',
  'www.youtube-nocookie.com',
  'youtu.be',
  'www.youtu.be'
];

const isYouTubeHost = (host = '') => YOUTUBE_HOSTS.includes(host.toLowerCase());

export const isValidVideoId = (value) => typeof value === 'string' && VIDEO_ID.test(value);
export const isValidPlaylistId = (value) => typeof value === 'string' && PLAYLIST_ID.test(value);

const toUrl = (input) => {
  const raw = String(input || '').trim();
  if (!raw) return null;
  try {
    return new URL(/^https?:\/\//i.test(raw) ? raw : `https://${raw}`);
  } catch {
    return null;
  }
};

const positiveInt = (value) => {
  const n = Number.parseInt(value, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * Pull the playlist id, the opening video and the 1-based position out of any
 * YouTube link (or a bare id). Returns null when there is no playlist in it —
 * a plain watch link belongs in the video block, not this one.
 */
export const parseYouTubePlaylist = (input) => {
  const raw = String(input || '').trim();
  if (!raw) return null;

  // A bare id pasted on its own: a playlist id, or a video id with no list.
  if (!/[/?:.\s]/.test(raw)) {
    if (isValidVideoId(raw)) return null;
    return isValidPlaylistId(raw) ? { playlistId: raw, videoId: '', index: null } : null;
  }

  const url = toUrl(raw);
  if (!url || !isYouTubeHost(url.hostname)) return null;

  const params = url.searchParams;
  const playlistId = (params.get('list') || '').trim();
  if (!isValidPlaylistId(playlistId)) return null;

  const path = url.pathname.replace(/\/+$/, '');
  let videoId = (params.get('v') || '').trim();

  if (!videoId) {
    // youtu.be/<id>, /embed/<id>, /shorts/<id>, /live/<id>
    const match = path.match(/^\/(?:embed|shorts|live|v)\/([^/]+)$/) || (
      /youtu\.be$/i.test(url.hostname) ? [null, path.replace(/^\//, '')] : null
    );
    const candidate = match ? match[1] : '';
    if (candidate && candidate !== 'videoseries') videoId = candidate;
  }

  return {
    playlistId,
    videoId: isValidVideoId(videoId) ? videoId : '',
    index: positiveInt(params.get('index'))
  };
};

/** The playlist's own page — the link to hand to students. */
export const playlistPageUrl = (playlistId) => (
  isValidPlaylistId(playlistId) ? `https://www.youtube.com/playlist?list=${playlistId}` : ''
);

/** A watch link that opens one video *inside* the playlist. */
export const playlistWatchUrl = ({ playlistId, videoId, index } = {}) => {
  if (!isValidPlaylistId(playlistId)) return '';
  if (!isValidVideoId(videoId)) return playlistPageUrl(playlistId);
  const position = positiveInt(index);
  return `https://www.youtube.com/watch?v=${videoId}&list=${playlistId}${position ? `&index=${position}` : ''}`;
};

/**
 * The player src. `videoseries` is YouTube's entry point for a playlist with no
 * particular starting video; naming a video instead opens on that one with the
 * rest of the list still queued behind it.
 *
 * Defaults to youtube-nocookie.com: an embedded playlist in a lesson is played
 * to a room, and the privacy-enhanced host does not set profiling cookies until
 * playback starts. PowerPoint wants the plain host, so it passes
 * `privacyMode: false`.
 */
export const playlistEmbedUrl = ({
  playlistId,
  videoId,
  index,
  privacyMode = true,
  autoplay = false
} = {}) => {
  if (!isValidPlaylistId(playlistId)) return '';
  const host = privacyMode ? 'https://www.youtube-nocookie.com' : 'https://www.youtube.com';
  const params = [`list=${playlistId}`, 'rel=0'];
  const position = positiveInt(index);

  let path;
  if (isValidVideoId(videoId)) {
    path = `/embed/${videoId}`;
  } else {
    path = '/embed/videoseries';
    if (position) params.push(`index=${position}`);
  }
  if (autoplay) params.push('autoplay=1');

  return `${host}${path}?${params.join('&')}`;
};

/**
 * Poster frame for one entry. `mq` (320x180) is a true 16:9 crop; `hq` is 4:3
 * with letterbox bars baked in, which looks stretched in a card.
 */
export const videoThumbnailUrl = (videoId, quality = 'mq') => (
  isValidVideoId(videoId) ? `https://i.ytimg.com/vi/${videoId}/${quality}default.jpg` : ''
);

/** "PT1M3S" (YouTube API) or a number of seconds → "1:03". */
export const formatDuration = (value) => {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'string' && /^\s*\d+:\d{2}(:\d{2})?\s*$/.test(value)) return value.trim();

  let seconds = null;
  if (typeof value === 'number') {
    seconds = value;
  } else {
    const iso = String(value).trim().match(/^P(?:(\d+)D)?T?(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?$/i);
    if (iso) {
      const [, d, h, m, s] = iso;
      seconds = (Number(d || 0) * 86400) + (Number(h || 0) * 3600) + (Number(m || 0) * 60) + Number(s || 0);
    } else if (/^\d+$/.test(String(value).trim())) {
      seconds = Number(String(value).trim());
    }
  }

  if (!Number.isFinite(seconds) || seconds < 0) return String(value).trim();
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  const pad = (n) => String(n).padStart(2, '0');
  return hours ? `${hours}:${pad(minutes)}:${pad(secs)}` : `${minutes}:${pad(secs)}`;
};

/**
 * Every YouTube video id in a blob of pasted text, in order and de-duplicated —
 * the "paste your links" path for building the track list by hand.
 */
export const parseVideoIdsFromText = (text) => {
  const found = [];
  const seen = new Set();
  const pattern = /(?:youtube\.com\/(?:watch\?(?:[^\s"']*&)?v=|embed\/|shorts\/|live\/|v\/)|youtu\.be\/)([A-Za-z0-9_-]{11})|(?:^|[\s,;])([A-Za-z0-9_-]{11})(?=$|[\s,;])/g;
  let match = pattern.exec(String(text || ''));
  while (match) {
    const id = match[1] || match[2];
    if (id && !seen.has(id)) {
      seen.add(id);
      found.push(id);
    }
    match = pattern.exec(String(text || ''));
  }
  return found;
};

let itemSeq = 0;
const nextItemId = () => {
  itemSeq += 1;
  return `ytpi_${Date.now().toString(36)}_${itemSeq.toString(36)}`;
};

/**
 * One row of the track list. `src` carries the poster URL under the name the
 * export's image walker looks for, so an exported lesson keeps its thumbnails
 * when it is opened offline.
 */
export const normalizePlaylistItem = (item = {}, fallbackIndex = 0) => {
  const videoId = isValidVideoId(item.videoId)
    ? item.videoId
    : (parseYouTubePlaylist(item.url || item.videoUrl || '')?.videoId
      || (isValidVideoId(String(item.url || '').trim()) ? String(item.url).trim() : ''));
  // A deck is a file people share, so the only poster accepted from one is an
  // inlined picture the export itself produced. Anything else is re-derived
  // from the video id.
  const existingThumb = typeof item.src === 'string' && /^data:image\/(png|jpe?g|webp|gif);/i.test(item.src)
    ? item.src
    : '';

  return {
    id: item.id || nextItemId(),
    videoId,
    title: String(item.title || '').trim(),
    duration: formatDuration(item.duration),
    note: String(item.note || '').trim(),
    position: Number.isFinite(Number(item.position)) ? Number(item.position) : fallbackIndex + 1,
    src: existingThumb || videoThumbnailUrl(videoId)
  };
};

export const normalizePlaylistItems = (items) => (
  Array.isArray(items) ? items.map((item, index) => normalizePlaylistItem(item, index)) : []
);

export const PLAYLIST_LAYOUTS = {
  split: 'Player + list side by side',
  stacked: 'Player above, list below',
  player: 'Player only',
  list: 'List only (handout style)'
};

export const DEFAULT_PLAYLIST_ACCENT = '#dc2626';

/**
 * The block as every renderer wants to read it: display strings resolved,
 * items normalized, URLs derived. Citation fields keep the video block's names
 * (videoTitle/videoAuthor/videoSource/videoDate/videoUrl) so the APA formatter,
 * the reference list and the detach-citation action all work unchanged.
 */
export const normalizePlaylistBlock = (block = {}) => {
  const playlistId = isValidPlaylistId(block.playlistId) ? block.playlistId : '';
  const items = normalizePlaylistItems(block.items);
  const startVideoId = isValidVideoId(block.startVideoId)
    ? block.startVideoId
    : (items.find((item) => item.videoId)?.videoId || '');
  const startIndex = positiveInt(block.startIndex);
  const countedItems = positiveInt(block.itemCount) || items.length;

  return {
    playlistId,
    items,
    startVideoId,
    startIndex,
    itemCount: countedItems,
    title: String(block.title || block.videoTitle || '').trim(),
    channel: String(block.channel || block.videoAuthor || '').trim(),
    description: String(block.description || '').trim(),
    layout: PLAYLIST_LAYOUTS[block.layout] ? block.layout : 'split',
    theme: block.theme === 'dark' ? 'dark' : 'light',
    accentColor: /^#[0-9a-f]{3,8}$/i.test(block.accentColor || '') ? block.accentColor : DEFAULT_PLAYLIST_ACCENT,
    aspectRatio: block.aspectRatio || '16-9',
    showThumbnails: block.showThumbnails !== false,
    playlistUrl: playlistPageUrl(playlistId),
    watchUrl: playlistWatchUrl({ playlistId, videoId: startVideoId, index: startIndex }),
    embedUrl: playlistEmbedUrl({ playlistId, videoId: startVideoId, index: startIndex }),
    includeCitation: block.videoIncludeCitation !== false
  };
};

/**
 * Whether the block carries enough of a source to be worth citing. Without
 * this the formatter degrades to a bare "(n.d.)" beside the playlist URL —
 * which the card already shows on its share line, so a fresh card would open
 * with a citation that says nothing.
 */
export const hasPlaylistCitationDetails = (block = {}) => (
  [block.videoAuthor, block.videoTitle, block.videoDate, block.title, block.channel]
    .some((value) => value && String(value).trim())
);

/** "14 videos" / "Video 3 of 14" — the subtitle under the playlist title. */
export const playlistCountLabel = ({ itemCount, startIndex } = {}) => {
  const total = positiveInt(itemCount);
  const position = positiveInt(startIndex);
  if (total && position && position <= total) return `Video ${position} of ${total}`;
  if (total) return `${total} video${total === 1 ? '' : 's'}`;
  return 'Playlist';
};
